import { useDebounceEffect } from 'ahooks';
import { useCallback, useRef, useState } from 'react';

import { ModuleSearchPostsReq, ModuleSupportCenterPost } from '@src/api/generated/data-contracts';
import { postSupportSearch } from '@src/api/generated/Support';
import { BASE_LANGUAGE } from '@src/pages/supportCenter/constants/language';

export interface SupportHomeSearchState {
  results: ModuleSupportCenterPost[];
  loading: boolean;
  totalCount: number;
}

export interface UseSupportHomeSearchReturn {
  state: SupportHomeSearchState;
  search: (keyword: string) => Promise<void>;
  reset: () => void;
}

const PAGE_SIZE = 10;

/**
 * SupportHome 页面搜索 hook
 * 只加载前 10 个结果，用于展示预览
 *
 * 语言固定为基准语言（英文）：首页没有语言入口，用户在这里无从表达语言意愿，
 * 跟着 localStorage 走会出现"首页界面全英文、搜索结果却是中文"的割裂。
 * 需要按其他语言搜索时，由帮助中心内页发起（内页有语言下拉）。
 */
function useSupportHomeSearch(keyword: string): UseSupportHomeSearchReturn {
  const [state, setState] = useState<SupportHomeSearchState>({
    results: [],
    loading: false,
    totalCount: 0,
  });

  // 竞态保护：只允许最新一次请求落地。快速改关键字（防抖触发）或重置时，
  // 旧请求后返回会覆盖新结果，导致列表显示过期数据。
  const searchSeq = useRef(0);

  // 清空搜索态：空关键字 / 业务失败 / 请求异常 / 主动重置 共用同一份默认状态
  const clearResults = useCallback(() => {
    setState({ results: [], loading: false, totalCount: 0 });
  }, []);

  // 搜索函数 - 只获取前 10 个
  const search = useCallback(async (searchKeyword: string) => {
    const seq = ++searchSeq.current;
    if (!searchKeyword.trim()) {
      clearResults();
      return;
    }

    setState(prev => ({ ...prev, loading: true }));
    try {
      const searchParams: ModuleSearchPostsReq = {
        keyword: searchKeyword.trim(),
        page: 0,
        pageSize: PAGE_SIZE,
        withContent: true,
        searchType: 'both',
        language: BASE_LANGUAGE,
      };

      const response = await postSupportSearch(searchParams);

      // 已有更新的请求发出，丢弃本次返回值
      if (seq !== searchSeq.current) return;

      if (response.code !== 0) {
        // 业务失败：后端一定带 msg，兜底把 code 打出来；异常分支单独打 error，两者可区分
        console.error('Search failed:', response.msg || `code=${response.code}`);
        clearResults();
        return;
      }

      // 成功：data 缺失只代表「没有结果」（protobuf 空 repeated 字段可能被序列化成字段缺失），
      // 不能当失败处理，否则会打出 msg 为 undefined 的误导性日志
      setState({
        results: response.data?.items || [],
        loading: false,
        totalCount: response.data?.pageInfo?.totalNumber || 0,
      });
    } catch (error) {
      if (seq !== searchSeq.current) return;
      console.error('Search error:', error);
      clearResults();
    }
  }, [clearResults]);

  // 重置搜索状态（同时作废在途请求，避免它返回后又把结果写回列表）
  const reset = useCallback(() => {
    searchSeq.current += 1;
    clearResults();
  }, [clearResults]);

  // 防抖搜索
  useDebounceEffect(
    () => {
      if (keyword) {
        search(keyword);
      } else {
        reset();
      }
    },
    [keyword],
    { wait: 300 },
  );

  return {
    state,
    search,
    reset,
  };
}

export default useSupportHomeSearch;
